import { router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { useRouteParams } from '@/hooks/useRoutes'
import type { ClusterRow } from './ClustersList'

interface PromoteClusterDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cluster: ClusterRow | null
}

/**
 * Opens when the agent clicks "Promote" on an AI-discovered cluster.
 * Lets them adjust the AI's suggested title/body/tags before saving as a
 * canned answer. Submit POSTs to the cluster promote endpoint.
 */
export function PromoteClusterDialog({ open, onOpenChange, cluster }: PromoteClusterDialogProps) {
  const { withAccount } = useRouteParams()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [tags, setTags] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !cluster) return
    setTitle(cluster.suggested_title ?? '')
    setBody(cluster.canonical_phrasing ?? '')
    setTags((cluster.suggested_tags ?? []).join(', '))
  }, [open, cluster])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!cluster) return
    setSaving(true)
    router.post(`/${withAccount().account_id}/shortcuts/clusters/${cluster.id}/promote`, {
      title,
      body,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean)
    }, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
      onFinish: () => setSaving(false)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Save as canned answer</DialogTitle>
          <DialogDescription>
            From an AI-discovered cluster of {cluster?.size} similar replies. Tweak the title and body if needed.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pc-title">Title</Label>
            <Input
              id="pc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short label, e.g. Merge duplicate profiles"
              autoFocus
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pc-body">Body</Label>
            <textarea
              id="pc-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              required
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
            <p className="text-xs text-muted-foreground">
              {`{{variable}}`} placeholders are filled inline when used. URLs in placeholder names (e.g. <code className="font-mono">{`{{profile_url}}`}</code>) auto-fill from the clipboard.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pc-tags">Tags</Label>
            <Input
              id="pc-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="merge, profiles"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="cursor-pointer">Cancel</Button>
            <Button type="submit" disabled={saving || !title || !body} className="cursor-pointer">
              {saving ? 'Saving…' : 'Save & enable'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
